import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { normalizeStatus } from '../../utils/statuses';
import PatientRow from './PatientRow';

const INITIAL_LIMIT = 10;

const COLOR_MAP = {
  orange: {
    border: 'border-l-orange-500',
    icon: 'text-orange-500',
    progress: 'bg-orange-500',
    count: 'text-orange-600 bg-orange-50',
    showAll: 'text-orange-600 hover:bg-orange-50',
  },
  red: {
    border: 'border-l-red-500',
    icon: 'text-red-500',
    progress: 'bg-red-500',
    count: 'text-red-600 bg-red-50',
    showAll: 'text-red-600 hover:bg-red-50',
  },
  amber: {
    border: 'border-l-amber-500',
    icon: 'text-amber-500',
    progress: 'bg-amber-500',
    count: 'text-amber-600 bg-amber-50',
    showAll: 'text-amber-600 hover:bg-amber-50',
  },
  emerald: {
    border: 'border-l-emerald-500',
    icon: 'text-emerald-500',
    progress: 'bg-emerald-500',
    count: 'text-emerald-600 bg-emerald-50',
    showAll: 'text-emerald-600 hover:bg-emerald-50',
  },
  blue: {
    border: 'border-l-blue-500',
    icon: 'text-blue-500',
    progress: 'bg-blue-500',
    count: 'text-blue-600 bg-blue-50',
    showAll: 'text-blue-600 hover:bg-blue-50',
  },
  purple: {
    border: 'border-l-purple-500',
    icon: 'text-purple-500',
    progress: 'bg-purple-500',
    count: 'text-purple-600 bg-purple-50',
    showAll: 'text-purple-600 hover:bg-purple-50',
  },
};

const RiskGroup = ({
  title,
  subtitle,
  icon: Icon,
  patients,
  isOpen,
  onToggle,
  accentColor,
  onPatientClick,
  onSendMessage,
  selectedIds,
  onToggleSelect,
  formatLastTouch,
  hideProgress = false,
  showAll = false,
  onShowAll,
  sectionRef,
  headerLink = null,
}) => {
  const contacted = patients.filter(p =>
    normalizeStatus(p.status) === 'contacted'
  ).length;

  const progress = patients.length > 0 ? (contacted / patients.length) * 100 : 0;

  const hasMore = patients.length > INITIAL_LIMIT;
  const visiblePatients = showAll ? patients : patients.slice(0, INITIAL_LIMIT);
  const remainingCount = patients.length - INITIAL_LIMIT;

  const colors = COLOR_MAP[accentColor] || COLOR_MAP.emerald;

  if (patients.length === 0) return null;

  return (
    <div ref={sectionRef} className={`bg-white rounded-lg shadow-sm border border-slate-200 border-l-4 ${colors.border} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${colors.icon}`} strokeWidth={1.5} />

          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${colors.count}`}>
                {patients.length}
              </span>
              {headerLink && (
                <Link
                  to={headerLink}
                  onClick={(e) => e.stopPropagation()}
                  className={`ml-2 flex items-center gap-1 text-xs font-medium ${colors.showAll}`}
                >
                  View all <ExternalLink className="h-3 w-3" />
                </Link>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {!hideProgress && (
            <div className="hidden sm:flex items-center gap-2">
              <div className="w-24 h-1 rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${colors.progress} transition-all duration-500`}
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-slate-400 tabular-nums">
                {contacted}/{patients.length}
              </span>
            </div>
          )}

          <ChevronDown
            className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
            strokeWidth={1.5}
          />
        </div>
      </button>

      {/* Patient List */}
      {isOpen && (
        <div className="border-t border-slate-100">
          {visiblePatients.map((patient) => (
            <PatientRow
              key={patient.id}
              patient={patient}
              isSelected={selectedIds.includes(patient.id)}
              onSelect={() => onToggleSelect(patient.id)}
              onClick={() => onPatientClick(patient)}
              onMessage={(e) => onSendMessage(patient, e)}
              formatLastTouch={formatLastTouch}
            />
          ))}

          {hasMore && !showAll && (
            <button
              onClick={onShowAll}
              className={`w-full px-5 py-3 text-sm font-medium ${colors.showAll} transition-colors border-t border-slate-100`}
            >
              Show all {patients.length} patients ({remainingCount} more)
            </button>
          )}

          {hasMore && showAll && (
            <button
              onClick={onShowAll}
              className={`w-full px-5 py-3 text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors border-t border-slate-100`}
            >
              Show less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RiskGroup;
